import React, { useEffect, useMemo, useState } from 'react';
import {
  closestCenter,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DroppableContainer
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ListView } from '@synerg/vdl-react-components';
import { nanoid } from 'nanoid';

// ============================================================================
// Types
// ============================================================================

/**
 * Wrapper that keeps original item data pristine while adding dnd-kit tracking.
 * Prefers item.id if present for stable React keys (important for focus management).
 */
interface DraggableWrapper {
  /** Stable ID for dnd-kit and React key. Uses item.id if present, otherwise generated. */
  id: string;
  /** Index of parent item (only set for children) */
  parentIndex?: number;
  /** Original item data, untouched */
  data: any;
  /** Wrapped children (only for parent items with nested items) */
  children?: DraggableWrapper[];
}

export interface IMDFDraggableListProps {
  items?: any[];
  itemComponent?: React.ClassType<any, any, any>;
  onChange?: (items: any[]) => void;
}

interface SortableItemProps {
  id: string;
  item: any;
  index: number;
  itemComponent?: React.ClassType<any, any, any>;
  children?: React.ReactNode;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generates a stable ID for an item.
 * Prefers item.id if present (for focus stability), otherwise generates one.
 */
function getItemId(item: any, prefix: string, index: number): string {
  if (item.id !== undefined && item.id !== null) {
    return `${prefix}-${item.id}`;
  }
  return `${prefix}-${index}-${nanoid(6)}`;
}

/**
 * Wrap items in DraggableWrapper objects, preserving original data.
 */
function wrapItems(items: any[]): DraggableWrapper[] {
  return items.map((item, index) => ({
    id: getItemId(item, 'p', index),
    data: item,
    children: item.items?.map((child: any, childIndex: number) => ({
      id: getItemId(child, 'c', childIndex),
      parentIndex: index,
      data: child
    }))
  }));
}

/**
 * Unwrap items back to original format for onChange callback.
 */
function unwrapItems(wrappers: DraggableWrapper[]): any[] {
  return wrappers.map((wrapper) => ({
    ...wrapper.data,
    items: wrapper.children?.map((child) => child.data)
  }));
}

/**
 * Parse an ID to determine hierarchy context.
 */
function parseId(id: string): { isChild: boolean; prefix: string } {
  return {
    isChild: id.startsWith('c-'),
    prefix: id.split('-')[0]
  };
}

/**
 * Find wrapper index by ID in a list.
 */
function findIndexById(wrappers: DraggableWrapper[], id: string): number {
  return wrappers.findIndex((w) => w.id === id);
}

/**
 * Custom collision detection that restricts drops to same hierarchy level.
 */
function createRestrictedCollisionDetection(wrappers: DraggableWrapper[]): CollisionDetection {
  return (args) => {
    const { active, droppableContainers } = args;
    const activeId = String(active.id);
    const activeIsChild = parseId(activeId).isChild;

    // Find parent index if active is a child
    let activeParentIndex: number | undefined;
    if (activeIsChild) {
      for (let i = 0; i < wrappers.length; i++) {
        const childIndex = wrappers[i].children?.findIndex((c) => c.id === activeId);
        if (childIndex !== undefined && childIndex >= 0) {
          activeParentIndex = i;
          break;
        }
      }
    }

    const validContainers: DroppableContainer[] = [];

    droppableContainers.forEach((container) => {
      const containerId = String(container.id);
      const containerIsChild = parseId(containerId).isChild;

      // Parent can only drop among parents
      if (!activeIsChild && !containerIsChild) {
        validContainers.push(container);
        return;
      }

      // Child can only drop within same parent's children
      if (activeIsChild && containerIsChild && activeParentIndex !== undefined) {
        const containerParentIndex = wrappers.findIndex(
          (w) => w.children?.some((c) => c.id === containerId)
        );
        if (containerParentIndex === activeParentIndex) {
          validContainers.push(container);
        }
      }
    });

    return closestCenter({
      ...args,
      droppableContainers: validContainers
    });
  };
}

// ============================================================================
// Sortable Item Components
// ============================================================================

const SortableParentItem: React.FC<SortableItemProps> = ({
  id,
  item,
  index,
  itemComponent: ItemComponent,
  children
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const dragHandleElementRef = React.useRef<HTMLElement | null>(null);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1
  };

  React.useEffect(() => {
    if (wrapperRef.current) {
      const handle = wrapperRef.current.querySelector('[data-drag-handle]');

      if (handle) {
        dragHandleElementRef.current = handle as HTMLElement;
      }
    }
  });

  const combinedRef = React.useCallback((node: HTMLDivElement | null) => {
    wrapperRef.current = node;
    setNodeRef(node);
  }, [setNodeRef]);

  const conditionalListeners: any = {};

  if (listeners) {
    Object.entries(listeners).forEach(([key, listener]) => {
      if (listener) {
        conditionalListeners[key] = (event: React.PointerEvent) => {
          const target = event.target as HTMLElement;
          const isFromHandle = dragHandleElementRef.current?.contains(target) || dragHandleElementRef.current === target;

          if (isFromHandle || !dragHandleElementRef.current) {
            (listener as any)(event);
          }
        };
      }
    });
  }

  const filteredAttributes = dragHandleElementRef.current
    ? Object.fromEntries(
      Object.entries(attributes || {}).filter(([key]) => key !== 'role' && key !== 'tabIndex')
    )
    : attributes;

  return (
    <div ref={combinedRef} style={style} data-index={index} {...filteredAttributes} {...conditionalListeners}>
      {ItemComponent ? (
        <ItemComponent itemIndex={index} item={item} />
      ) : (
        <ListView
          status={item.status}
          selectable={item.selectable}
          showCheckbox={item.showCheckbox}
          disabled={item.disabled}
        >
          {item.label}
          {children}
        </ListView>
      )}
      {ItemComponent && children}
    </div>
  );
};

const SortableChildItem: React.FC<Omit<SortableItemProps, 'itemComponent' | 'children'> & { parentIndex: number }> = ({
  id,
  item,
  index,
  parentIndex
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-parent-index={parentIndex}
      data-index={index}
      {...attributes}
      {...listeners}
    >
      <ListView
        status={item.status}
        selectable={item.selectable}
        showCheckbox={item.showCheckbox}
        disabled={item.disabled}
      >
        {item.label}
      </ListView>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const MDFDraggableList2 = (props: IMDFDraggableListProps) => {
  const { items, itemComponent: ItemComponent, onChange } = props;
  const [wrappers, setWrappers] = useState<DraggableWrapper[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeData, setActiveData] = useState<any>(null);
  const [focusedElementId, setFocusedElementId] = useState<string | null>(null);

  // Wrap items when props change
  useEffect(() => {
    if (items) {
      setWrappers(wrapItems(items));
    }
    else {
      setWrappers([]);
    }
  }, [items]);

  // Memoize collision detection (depends on wrappers for parent lookup)
  const collisionDetection = useMemo(
    () => createRestrictedCollisionDetection(wrappers),
    [wrappers]
  );

  // Extract parent IDs for top-level SortableContext
  const parentIds = useMemo(() => wrappers.map((w) => w.id), [wrappers]);

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    setActiveId(id);

    const activeElement = document.activeElement as HTMLElement;

    if (activeElement) {
      const focusId = activeElement.getAttribute('id') || `focus-${Date.now()}`;
      activeElement.setAttribute('id', focusId);
      setFocusedElementId(focusId);
    }

    // Find the item being dragged for the overlay
    const isChild = parseId(id).isChild;

    if (isChild) {
      for (const wrapper of wrappers) {
        const child = wrapper.children?.find((c) => c.id === id);

        if (child) {
          setActiveData(child.data);
          break;
        }
      }
    }
    else {
      const wrapper = wrappers.find((w) => w.id === id);
      if (wrapper) {
        setActiveData(wrapper.data);
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    setActiveData(null);

    if (!over || active.id === over.id) {
      return;
    }

    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);
    const activeIsChild = parseId(activeIdStr).isChild;
    const overIsChild = parseId(overIdStr).isChild;

    setWrappers((currentWrappers) => {
      let updatedWrappers: DraggableWrapper[];

      if (activeIsChild && overIsChild) {
        // Find which parent contains these children
        const parentIndex = currentWrappers.findIndex(
          (w) => w.children?.some((c) => c.id === activeIdStr)
        );

        if (parentIndex === -1) {
          return currentWrappers;
        }

        const parent = currentWrappers[parentIndex];
        if (!parent.children) {
          return currentWrappers;
        }

        const oldIndex = findIndexById(parent.children, activeIdStr);
        const newIndex = findIndexById(parent.children, overIdStr);

        if (oldIndex === -1 || newIndex === -1) {
          return currentWrappers;
        }

        const reorderedChildren = arrayMove(parent.children, oldIndex, newIndex);

        updatedWrappers = currentWrappers.map((w, idx) =>
          idx === parentIndex ? { ...w, children: reorderedChildren } : w
        );
      }
      else if (!activeIsChild && !overIsChild) {
        // Reorder parents
        const oldIndex = findIndexById(currentWrappers, activeIdStr);
        const newIndex = findIndexById(currentWrappers, overIdStr);

        if (oldIndex === -1 || newIndex === -1) {
          return currentWrappers;
        }

        updatedWrappers = arrayMove(currentWrappers, oldIndex, newIndex);
      }
      else {
        // Mixed hierarchy - shouldn't happen
        return currentWrappers;
      }

      // Notify parent with unwrapped data
      onChange?.(unwrapItems(updatedWrappers));

      setTimeout(() => {
        if (focusedElementId) {
          const element = document.querySelector(`[id="${focusedElementId}"]`);

          if (element instanceof HTMLElement) {
            if (typeof (element as any).setFocus === 'function') {
              (element as any).setFocus();
            }
            else if (element instanceof HTMLElement) {
              element.focus();
            }
          }

          setFocusedElementId(null);
        }
      }, 10);

      return updatedWrappers;
    });
  };

  return (
    <DndContext
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="container">
        <div className="nested">
          <SortableContext items={parentIds} strategy={verticalListSortingStrategy}>
            {wrappers.map((wrapper, parentIndex) => {
              return (
                <SortableParentItem
                  key={wrapper.id}
                  id={wrapper.id}
                  item={wrapper.data}
                  index={parentIndex}
                  itemComponent={ItemComponent}
                >
                  {wrapper.children && wrapper.children.length > 0 && (
                    <div className="nested">
                      <SortableContext
                        items={wrapper.children.map((child) => child.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {wrapper.children.map((child, childIndex) => (
                          <SortableChildItem
                            key={child.id}
                            id={child.id}
                            item={child.data}
                            index={childIndex}
                            parentIndex={parentIndex}
                          />
                        ))}
                      </SortableContext>
                    </div>
                  )}
                </SortableParentItem>
              );
            })}
          </SortableContext>
        </div>
      </div>

      <DragOverlay>
        {activeId && activeData ? (
          <div style={{ opacity: 0.8, cursor: 'grabbing' }}>
            <ListView
              status={activeData.status}
              selectable={activeData.selectable}
              showCheckbox={activeData.showCheckbox}
              disabled={activeData.disabled}
            >
              {activeData.label}
            </ListView>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

MDFDraggableList2.displayName = 'MDFDraggableList2';
